// Composer that builds a single pdfmake doc-def for the Manage Budgets
// (Edit Budgets) page — one section per in-page tab the user ticks in the
// SharePdfButton dropdown. Used by BudgetCostCodes' SharePdfButton
// renderPdfMakeDoc; returns null when no section is selected (SharePdfButton
// guards this too).
//
// Format deliberately MATCHES the Budget Overview print: the page wrapper
// (A4 landscape, pageMargins [28,32,28,36], grey footer "Printed on … |
// <Dept> Budget | Crew Forge" + "Page X of Y", info.author = company,
// defaultStyle {fontSize:9,color:'#000'}, navy #1e3a5f centred title strip)
// is copied from budgetVariancePdf.js, and the navy table header / cell
// styling is copied from budgetReportPdf.js.
//
// Page-break behaviour: sections are appended in SECTION order; every
// section after the first gets `pageBreak: 'before'` on its first node so
// each tab starts on a fresh page.
//
// No dependency on pdfmake itself: only declarative doc-def data is returned,
// so the layout stays pure and unit-testable.

import { formatDate } from './dateFormat';
import { ALLOCATION_OPTIONS, COMPUTER_ALLOWANCE_LABEL, COMPUTER_ALLOWANCE_NAME } from '../constants/budget';
/* 🔑 IMPORTED, NOT RE-ROLLED. The owner asked for the 6th/7th-day columns to
   be squeezed in by narrowing Name and Location, which needs a truncation rule
   — and one already exists for exactly this data: the Gantt's bar label. Its
   15-character limit is code-point aware (a `.slice` splits a surrogate pair
   and renders a replacement glyph) and reusing it means the printed name and
   the on-screen one break at the same place, which is worth more here than a
   width tuned to this table. `utils/` importing from `components/` is the
   unusual direction; the alternative was a second truncation rule, which the
   Code Unification Principle forbids and which would drift the first time
   either is retuned. No cycle: `CrewGanttView.js` imports nothing from here. */
import { truncateCrewName, CREW_NAME_MAX_CHARS } from '../components/budget/CrewGanttView';

const NAVY = '#1e3a5f';
const ZEBRA = '#f8f8f8';
const SUBTOTAL_BG = '#f0f0f0';
const GREY_RULE = '#dddddd';
const FOOTER_GREY = '#999999';

// Local (non-UTC) short date for the footer — mirrors budgetVariancePdf.js.
// Uses the browser locale's calendar day, so no toISOString()/UTC drift.
const dateShort = () => new Date().toLocaleDateString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric',
});

// ── Shared navy table styling (copied from budgetReportPdf.js) ──────────
const headerCell = (text, alignment = 'left') => ({
  text, fillColor: NAVY, color: 'white', bold: true, fontSize: 7.5, alignment,
});
const cell = (text, alignment = 'left', extra = {}) => ({
  text: text == null || text === '' ? '' : text, fontSize: 8, alignment, ...extra,
});
const totalCell = (text, alignment = 'left', colSpan) => ({
  text, fillColor: SUBTOTAL_BG, bold: true, fontSize: 8, alignment,
  ...(colSpan ? { colSpan } : {}),
});

// Shared table layout — zebra rows + thin grey rules, matching the Cost
// Report table in budgetReportPdf.js.
const TABLE_LAYOUT = {
  fillColor: (rowIndex) => (rowIndex > 0 && rowIndex % 2 === 0 ? ZEBRA : null),
  hLineColor: () => GREY_RULE,
  vLineColor: () => GREY_RULE,
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  paddingTop: () => 2,
  paddingBottom: () => 2,
  paddingLeft: () => 4,
  paddingRight: () => 4,
};

// A bold navy section heading (the tab label) that opens every section.
const sectionHeading = (label) => ({
  text: label, fontSize: 13, bold: true, color: NAVY, margin: [0, 0, 0, 6],
});

const num = (v) => parseFloat(v) || 0;

const allocLabel = (val) => {
  const opt = ALLOCATION_OPTIONS.find(o => o.value === parseFloat(val));
  return opt ? opt.label : (val == null || val === '' ? '—' : String(val));
};

/* ═══ THE PRINTED LOCATION LIMIT (2026-08-23) ════════════════════════
   Owner, asking for the two premium columns: *"'Name' and 'Location' column
   widths can likely be reduced and truncated to make it fit."*

   🔑 7, NOT the name's 15, AND THE DIFFERENCE IS MEASURED RATHER THAN
   CHOSEN. The Location column is 34pt wide, which holds 7 lowercase glyphs of
   Roboto at 8pt ("Pinewo…" is 31.6pt); the Name column is 105.4pt and holds
   its 15. 🔄 It was 10 chars in 46pt until the allowance-source prefixes
   needed the width, and this is the column the owner named first. Measured with
   pdfmake's own bundled Roboto through `@foliojs-fork/pdfkit` — the engine it
   renders with — by `scripts/measure-labour-flow-pdf-columns.js`.

   🔻 LOCATION GIVES WAY FIRST, DELIBERATELY. A truncated NAME in a PDF is
   unrecoverable (print has no hover), so of the two columns the owner offered,
   the one carrying less identity takes the bigger cut. Measured on the dev
   database, `crew_members.location` is EMPTY on all 150 rows while `name`
   averages 12.7 characters and peaks at 20 — so this limit costs nothing today
   and the name limit bites only the longest few.

   🚨 NEITHER LIMIT MAY EVER REACH A NUMBER. Only Name and Location are
   truncated; every money and count cell prints in full, and the widths below
   are sized so they fit. A clipped figure on a signed-off page is a different
   class of harm from a shortened name. */
const PDF_LOCATION_MAX_CHARS = 7;

/* ═══ A PREMIUM CELL: THE COUNT BESIDE THE MONEY ════════════════════
   🔑 BOTH, because a premium COST without its COUNT cannot be sanity-checked
   on paper, and the on-screen cost summary already shows both
   (`6th days · 2 · £350.00`). The widest realistic value, `30 · £11,111.10`,
   measures 54.9pt against the 58pt column — so it fits, which is why the count
   is here rather than dropped.

   🪤 `0` IS A REAL VALUE, so this is `Number.isFinite`, never a falsy test
   and never `count || '—'`. A member with no premium days prints `0 · £0.00`
   — never blank, never omitted — matching the owner's ruling for the on-screen
   figures: *"6th and 7th days should show 0 days and £0 if no 6th or 7 days
   exist for that person."* The server sends `0` (not null) for these two counts
   on the week-fraction basis as well, so there is no absent case to guard.

   🔴 IT DOES NOT DISTINGUISH AN UNPRICED PREMIUM DAY, AND THAT IS A RULING,
   NOT AN OVERSIGHT. On screen an amber triangle separates *"worked, no rate
   set"* from *"worked, rate is zero"*; print has no tooltip, so the question was
   put to the owner. His answer, 2026-08-23: *"I think £0.00 should not be a
   option. 6th and 7th days are either blank = no cap or must have a value"* and
   *"as you said there should be no free days."* A zero premium RATE is being
   made invalid on its own branch, so the second of those two states is going
   away — leaving £0.00 reachable only for a member with no weekly rate at all.
   Building a marker for a state that will not occur would leave the next reader
   maintaining a branch that can never be reached. It prints the published
   figures and nothing else. */
/* ═══ WHAT THE ALLOWANCE FIGURE IS MADE OF (2026-08-23) ════════════════
   Owner: *"allowance column make slightly wider and put the first 3 letters of
   each allowance in such as 'com,box,pho £569' or something similar so we can
   see if the allowance is made up from multiple sources. drop it to 2 letters
   if tight."*

   🔑 THE POINT IS THE COMPOSITE. `£569.00` alone cannot be told from one
   allowance or four, and print has no hover to ask. The prefixes make the
   cardinality visible; they are an AID, and the figure is the fact.

   🔴 COLLISIONS ARE ACCEPTED — OWNER RULING, 2026-08-23, AND THIS LINE EXISTS
   SO NOBODY "FIXES" IT: *"dont worry about clashing allowance abbreviations
   like 'tra, tra' as the buyer will quickly realise and change their naming
   convention to make it make sense."* `Travel` and `Transport` both give `tra`
   and that is FINE, because the person reading the page is the person who
   named them and can rename one. There is deliberately NO fourth letter, NO
   full-name fallback and NO index suffix. A disambiguation scheme here would be
   invented complexity solving a problem its own users can solve better.

   🪤 ONLY SOURCES THAT CONTRIBUTE ARE LISTED, and the test is NON-ZERO rather
   than the `> 0` originally specified. They behave identically on every row in
   the data (measured: 0 negative allowance or rental amounts in 150 crew rows,
   and no negative path found in the write route), so this costs nothing — but
   if a negative ever becomes reachable, `> 0` would drop it from the list while
   its money stayed inside the figure, which is precisely the "the number hides
   what it is made of" defect this feature exists to remove. A named allowance
   costing £0 IS excluded: it contributed nothing, and listing it would claim
   it did.

   🔑 SLOT ORDER (computer, 1, 2, 3), NEVER SIZE ORDER, so the same member
   reads the same way every time and two rows can be compared down the page.

   🔑 LOWER CASE, following the owner's own example. The source names are
   Title Case (`Box`, `Phone`), and `Box` cut to three letters is still exactly
   `Box` — a complete-looking word that reads as the whole name. `box` reads as
   a token, which is what it is. It also makes the list scan as one compact
   code rather than as a row of truncated proper nouns beside full-cased text
   elsewhere in the table.

   ⚠️ Declared limit: the prefix is a fixed 3 code points, so a name shorter
   than that prints in full (`Box` → `box`) and one that is entirely emoji or
   CJK prints 3 of those. Code-point aware (`[...str]`), never `.slice`, so a
   surrogate pair is never cut in half into a replacement glyph. */
const ALLOWANCE_PREFIX_CHARS = 3;

/* 🪤 THE COMPUTER SLOT HAS NO NAME COLUMN — its name is locked in the crew
   form (`BudgetCostCodes.js`, allowance row #0, `disabled`), so the literal
   lives on the client rather than being read from a row.
   🔻 IT WAS A PRIVATE COPY HERE UNTIL 2026-08-23, carrying its own comment
   saying "if that locked label is ever changed, this must change with it".
   Two branches landed the same hour: one added that copy, the other made the
   string a shared export — and because they touched different files, the merge
   was CLEAN and would have left both, silently, which is exactly the drift the
   old comment was warning about. Imported from `constants/budget.js` now, so
   the warning is enforced by the module system instead of by remembering. */

/* The same fallback the on-screen breakdown uses for a slot carrying an amount
   but no name, so the two surfaces call it the same thing. */
const UNNAMED_ALLOWANCE = 'Allowance';

const allowancePrefix = (name) => [...(String(name ?? '').trim() || UNNAMED_ALLOWANCE)]
  .slice(0, ALLOWANCE_PREFIX_CHARS)
  .join('')
  .toLowerCase();

/**
 * The Allowances cell: `com,box,pho £569.00`, or just `£569.00` when nothing
 * contributed.
 *
 * 🚨 THE PREFIXES ARE A SEPARATE, SMALLER, GREY RUN — 6.5pt against the
 * cell's 8pt — AND THAT IS LOAD-BEARING TWICE OVER. It states the hierarchy
 * (aid vs fact) in the one channel print has, and it is what makes the column
 * fit at all: four 3-letter prefixes plus a realistic figure measure 87.3pt at
 * 6.5pt against 98.2pt at 8pt, and only the first fits the 93pt column without
 * taking width from `Name`, which this arrangement leaves untouched.
 *
 * 🪤 THE MONEY CAN NEVER BE SPLIT, and that is a property of the widths, not
 * of this function. The figure alone is at most 50.4pt (`£1,234,567.89`) in a
 * 93pt column, so it always fits one line; the only break pdfmake can take is
 * the SPACE between the prefixes and the figure, which pushes the prefixes onto
 * their own line and leaves the number whole. Prefixes give way, the number
 * never does — by construction rather than by a length check.
 */
const allowancesCell = (member, total, formatMoney) => {
  // 🔀 GRADE ALLOWANCES (2026-10-25): the slot NAME is the SERVER-RESOLVED one
  // (`member.allowances.allowance_N.name`), never the raw member column — a
  // member FOLLOWING the grade stores NULL there and is costed under the
  // GRADE's name. Absent (an older payload) → no name, so the prefix degrades.
  const resolvedName = (key) => (member.allowances && member.allowances[key] ? member.allowances[key].name : null);
  const sources = [
    { name: COMPUTER_ALLOWANCE_NAME, cost: num(member.computer_cost) },
    { name: resolvedName('allowance_1'), cost: num(member.allowance_cost) },
    { name: resolvedName('allowance_2'), cost: num(member.allowance_cost_2) },
    { name: resolvedName('allowance_3'), cost: num(member.allowance_cost_3) },
  ].filter((a) => a.cost !== 0);
  const money = formatMoney(total);
  if (sources.length === 0) return money;
  return [
    {
      text: `${sources.map((a) => allowancePrefix(a.name)).join(',')} `,
      fontSize: 6.5,
      color: '#666666',
    },
    money,
  ];
};

const premiumCell = (count, cost, formatMoney) => {
  const n = Number(count);
  return `${Number.isFinite(n) ? n : 0} · ${formatMoney(num(cost))}`;
};

// ── Section builders — each returns a pdfmake content[] slice ───────────

export function buildCostCodesContent(rows = [], { formatMoney }) {
  const widths = [55, '*', '*', 75, 75];
  const body = [[
    headerCell('Code'), headerCell('Name'), headerCell('Description'),
    headerCell('Budget', 'right'), headerCell('Spent to Date', 'right'),
  ]];
  let totalBudget = 0;
  let totalSpent = 0;
  rows.forEach(cc => {
    const budget = num(cc.budget_amount);
    const spent = num(cc.expenditure_to_date);
    totalBudget += budget;
    totalSpent += spent;
    body.push([
      cell(cc.code || '—'),
      cell(cc.name || '—'),
      cell(cc.description || '—'),
      cell(formatMoney(budget), 'right'),
      cell(formatMoney(spent), 'right'),
    ]);
  });
  body.push([
    totalCell('Total', 'left', 3), {}, {},
    totalCell(formatMoney(totalBudget), 'right'),
    totalCell(formatMoney(totalSpent), 'right'),
  ]);
  return [
    sectionHeading('Cost Codes & Budget'),
    { table: { headerRows: 1, widths, body, dontBreakRows: true }, layout: TABLE_LAYOUT },
  ];
}

export function buildPaymentTypesContent(rows = []) {
  const widths = ['*', '*'];
  const body = [[headerCell('Name'), headerCell('Description')]];
  rows.forEach(pt => {
    body.push([cell(pt.name || '—'), cell(pt.description || '—')]);
  });
  return [
    sectionHeading('Payment Types'),
    { table: { headerRows: 1, widths, body, dontBreakRows: true }, layout: TABLE_LAYOUT },
  ];
}

export function buildCrewGradesContent(rows = [], { formatMoney }) {
  /* ═══ THE GRADE'S DEFAULT FOR THE *COMPUTER ALLOWANCE* (2026-08-23) ══════════
     🔻 This column's header read `Computer Rental`, and the block below in
     `buildCrewMembersContent` said in terms that it was *"left exactly as it
     is"*. That was right about the rename it was refusing — the crew column
     became `Allowances` and this one is a per-week RATE, so it could not take
     the same word — but it left the header presenting computer rental as a COST
     CATEGORY of the grade, beside `Weekly Rate`, which is the presentation the
     owner has ruled against three times: *"remember computer is one type of
     allowance and not different. Its just a common allowance so that's why its
     included as a default example."*

     🔑 IT IS NAMED AS WHAT IT IS, not renamed to `Allowances`: the column prints
     `crew_grades.computer_rental` alone. 🔻 CORRECTED 2026-10-25 (review F6):
     this said `crew_grades` had no `allowance_*` columns — FALSE since grade
     allowances (it carries the three named slots and their caps too), so the
     column is truthful but INCOMPLETE; whether it becomes "Total allowances"
     is an OWNER question, open, not settled here.

     🪤 A RATE IS NOT A COST, and the ruling does not merge those. This is
     `crew_grades.computer_rental`, £/week, an INPUT; `computer_cost` is what the
     engagement comes to. It stays a rate column and stays in this table.

     📏 The width is UNCHANGED at 100. `Computer Allowance` is three characters
     longer than `Computer Rental` — at `headerCell`'s 7.5pt bold that is roughly
     74pt of text inside a 100pt content column less 8pt of `TABLE_LAYOUT`
     padding, i.e. about 18pt clear. ⚠️ THAT IS A CHARACTER-WIDTH ESTIMATE, NOT A
     MEASUREMENT IN A RENDERED PDF — this file's crew-table widths were measured
     properly by `scripts/measure-labour-flow-pdf-columns.js`, and if this header
     ever wraps that script is how to re-derive it.
     ONE label, shared with the four screen sites: `constants/budget.js`. */
  const widths = ['*', 85, 100, '*'];
  const body = [[
    headerCell('Grade Name'),
    headerCell('Weekly Rate', 'right'),
    headerCell(COMPUTER_ALLOWANCE_LABEL, 'right'),
    headerCell('Default Access Rights'),
  ]];
  rows.forEach(g => {
    body.push([
      cell(g.name || '—'),
      cell(formatMoney(num(g.weekly_rate)), 'right'),
      cell(formatMoney(num(g.computer_rental)), 'right'),
      cell(g.default_role_name || 'None'),
    ]);
  });
  return [
    sectionHeading('Crew Grades'),
    { table: { headerRows: 1, widths, body, dontBreakRows: true }, layout: TABLE_LAYOUT },
  ];
}

export function buildCrewMembersContent(rows = [], { formatMoney }) {
  /* ═══ COLUMN WIDTHS — MEASURED, NOT ESTIMATED (2026-08-23) ══════════════
     Owner: *"try and squeeze in '6th day' and '7th day' columns into 'Name
     Grade Location Start End Alloc Weeks Rate/wk Budget Computer'. 'Name' and
     'Location' column widths can likely be reduced and truncated to make it
     fit."*

     📏 THE ARITHMETIC, so the next person can re-tune it without re-deriving
     it. A4 landscape is 841.89pt wide; `pageMargins` [28,32,28,36] leaves
     785.89pt. pdfmake's declared widths are CONTENT widths — `TABLE_LAYOUT`'s
     4+4 padding and 0.5 vertical rules are added on top — so 12 columns cost
     12×8 + 13×0.5 = 102.5pt of overhead. The eleven fixed widths total 578pt,
     which leaves the `*` Name column exactly 105.39pt.

     🔑 `Name` STAYS `*` AND MUST. A star column absorbs the remainder, so the
     table is exactly page-width whatever the other eleven are set to; hard-
     coding all twelve to sum to the page area is the overflow
     `budgetReportPdf.test.js` already has a regression for.

     📏 MEASURED with pdfmake's own bundled Roboto via `@foliojs-fork/pdfkit`
     (`scripts/measure-labour-flow-pdf-columns.js`, re-runnable). Every column
     fits its header AND its worst realistic content: `Workshop Supervisor`
     (the longest grade in the dev database) 75.8pt in 78; `06 Mar 2026` 45.0pt
     in 48; `£1,234,567.89` 50.4pt in 60; `30 · £11,111.10` 54.9pt in 58;
     the `Allowances` HEADER 38.6pt in 58. Nothing wraps and no figure clips.
     🔻 A CORRECTION TO THIS BLOCK'S OWN EARLIER CLAIM. It said a 15-character
     all-capitals name "measures ~110pt and would WRAP". IT DOES NOT WRAP. That
     figure measured 15 letters PLUS the ellipsis — 16 code points — whereas
     `truncateCrewName` emits 14 letters plus the ellipsis, 15 in total. The
     real worst case is `MMMMMMMMMMMMMM…` at 103.1pt and `WWWWWWWWWWWWWW…` at
     104.2pt, both inside 105.39pt, so the Name column does not wrap even in
     the pathological case. The script prints those two figures every run.

     🔻 WHAT GAVE WAY, in the order the owner asked for: Location 75→46 and
     truncated to 10 chars, Grade 85→78, Start/End 60→48, Alloc 48→40, Weeks
     40→32, Rate/wk 65→52, Budget 72→60, Allowances 65→58, and Name from a
     130.39pt star to a 105.39pt one with a 15-char truncation. No money or
     count column was narrowed below its widest realistic value.

     🔄 RE-TUNED SAME DAY for the allowance-source prefixes (owner: *"allowance
     column make slightly wider … We can still save width space on 'Name' if
     required."*). Allowances 58→91; paid for by Location 46→36 (10→8 chars),
     Alloc 40→36, Weeks 32→26, Rate/wk 52→44, Budget 60→55.

     ✅ `Name` WAS NOT TOUCHED, AND THAT IS THE POINT OF THE ARRANGEMENT. The
     eleven fixed widths still total 578pt, so the `*` column is still exactly
     105.39pt and the worst-case 15-character all-capitals name behaves exactly
     as it did before — the offer of Name width was there and was not needed.
     ⚠️ Anyone re-tuning this must keep the fixed total at 578, or say plainly
     what they did to Name. */
  const widths = ['*', 78, 34, 48, 48, 36, 26, 44, 55, 58, 58, 93];
  const body = [[
    headerCell('Name'), headerCell('Grade'), headerCell('Location'),
    headerCell('Start'), headerCell('End'), headerCell('Alloc', 'center'),
    headerCell('Weeks', 'right'), headerCell('Rate/wk', 'right'),
    headerCell('Budget', 'right'),
    /* 🔴 A BREAKDOWN OF `Budget`, NOT AN ADDITION TO IT. `budget_cost` already
       CONTAINS the premium — `computeCrewMemberCost` REPLACES those days'
       standard pay, it does not append a line (`crewMemberCostFields.js` states
       it at `member_total`). So these two columns are part of the Budget beside
       them and adding all three double-pays every premium day. Flagged to the
       owner; the labels are his words verbatim. */
    headerCell('6th day', 'right'), headerCell('7th day', 'right'),
    headerCell('Allowances', 'right'),
  ]];
  let totalLabour = 0;
  let totalAllowances = 0;
  let totalSixthDays = 0;
  let totalSixthLabour = 0;
  let totalSeventhDays = 0;
  let totalSeventhLabour = 0;
  rows.forEach(m => {
    const budget = num(m.budget_cost);
    /* ═══ THIS COLUMN IS `Allowances`, AND IT IS THE WHOLE GROUPING (2026-08-23) ═
       Owner: *"audit pdf and print. it still says computer in the column
       rather than 'Allowances'"*. He renamed the crew table's column the day
       before; the PDF was not in that change's scope and kept the old one.

       🔑 COMPUTER RENTAL **IS** AN ALLOWANCE. Owner, same day:
       *"remember computer is one type of allowance and not different. Its
       just a common allowance so thats why its included as a default
       example."* The built-in Computer Rental slot is allowance #0 with its
       name locked, so `allowance_total = computer_cost + a1 + a2 + a3` is
       correct BY THE DOMAIN and this column showing it is the PDF agreeing
       with the domain rather than adopting a preference. It is the same
       field, for the same reason, that `BudgetCostCodes.js`'s crew table and
       the cost-summary panel read.

       🔑 READ, NEVER RE-DERIVED. The server publishes `allowance_total`
       (`helpers/crewMemberCostFields.js`) as a `round2` of four
       already-rounded components. Summing those four here would be one more
       copy of a cost formula in a directory that only REPORTS money — the
       shape `server/helpers/hireCost.js` records as having shipped a live
       understatement.

       🚨 AND THERE IS DELIBERATELY NO `?? m.computer_cost` FALLBACK. The
       tempting one is the device `budgetReportPdf.js` uses for STORED version
       aggregates that predate the field; these rows come straight off
       `GET /api/budget/crew-members`, which always publishes it. A fallback
       would print the laptop alone under a heading claiming every allowance
       — a plausible, silently understated figure on a document nobody
       re-adds. An absent field printing £0.00 is at least visibly wrong.

       🪤 A RATE IS NOT A COST, so this rename does NOT travel:
       `buildCrewGradesContent`'s column above is `crew_grades.computer_rental`,
       £/week, an INPUT, and it stays a per-week rate column.
       🔀 CORRECTED 2026-08-23 — it said that column was *"left exactly as it is"*
       and named it `Computer Rental`; it is now `Computer Allowance`, and the
       matching crew-grade card on screen is `Computer Allowance/wk`. The
       rate-is-not-a-cost rule above is UNCHANGED and is exactly why that column
       did NOT become `Allowances`; what changed is only whether the heading
       presents computer as a separate CATEGORY. See that function's docblock. */
    const allowances = num(m.allowance_total);
    /* 🚨 READ, NEVER DERIVED. All four premium figures are published by
       `GET /api/budget/crew-members` and this file prints them. It must never
       compute a premium from a rate and a day count: the server resolves the
       1.5×/2× default, the member override and the unpriced case
       (`helpers/crewCost.js`), and a second opinion about premium money in a
       directory that only reports it is the shape that has already shipped a
       live understatement here. */
    const sixthDays = num(m.sixth_day_count);
    const seventhDays = num(m.seventh_day_count);
    totalLabour += budget;
    totalAllowances += allowances;
    totalSixthDays += sixthDays;
    totalSixthLabour += num(m.sixth_day_labour);
    totalSeventhDays += seventhDays;
    totalSeventhLabour += num(m.seventh_day_labour);
    const rate = m.effective_weekly_rate != null ? m.effective_weekly_rate : m.grade_weekly_rate;
    body.push([
      cell(m.name ? truncateCrewName(m.name, CREW_NAME_MAX_CHARS) : '—'),
      cell(m.grade_name || '—'),
      cell(m.location ? truncateCrewName(m.location, PDF_LOCATION_MAX_CHARS) : '—'),
      cell(m.start_date ? formatDate(m.start_date) : '—'),
      cell(m.end_date ? formatDate(m.end_date) : '—'),
      cell(allocLabel(m.default_allocation), 'center'),
      cell(m.total_weeks != null ? String(m.total_weeks) : '—', 'right'),
      cell(rate != null ? formatMoney(num(rate)) : '—', 'right'),
      cell(formatMoney(budget), 'right'),
      cell(premiumCell(m.sixth_day_count, m.sixth_day_labour, formatMoney), 'right'),
      cell(premiumCell(m.seventh_day_count, m.seventh_day_labour, formatMoney), 'right'),
      cell(allowancesCell(m, allowances, formatMoney), 'right'),
    ]);
  });
  /* The column total is the sum of exactly what the column printed, row for
     row. 🟢 THERE IS NO GRAND TOTAL IN THIS PDF and this change did not add
     one — checked, not assumed: the composer is handed a `data.totals`
     ({ totalLabour, totalComputer, crewGrandTotal, totalBudget })
     that NO section builder reads, and each section prints only its own
     per-column `Total` row. So the page footer's `crewGrandTotal` — which
     counts computer as its OWN term and would double-count every laptop if
     `allowance_total` were folded into it (measured £3,755 against £3,455) —
     has no counterpart here to get wrong. If a grand total is ever added to
     this PDF, it must take `budget_cost + allowance_total` per member, or the
     published `member_total`, and MUST NOT add a separate computer term. */
  body.push([
    totalCell('Total', 'left', 8), {}, {}, {}, {}, {}, {}, {},
    totalCell(formatMoney(totalLabour), 'right'),
    totalCell(premiumCell(totalSixthDays, totalSixthLabour, formatMoney), 'right'),
    totalCell(premiumCell(totalSeventhDays, totalSeventhLabour, formatMoney), 'right'),
    /* 🔻 NO PREFIXES ON THE TOTAL, deliberately. Composition is a fact about
       a MEMBER; the column total is every allowance in the department, so a
       prefix list here would either repeat every distinct name or claim a
       composition that belongs to nobody. The rows explain themselves and the
       total stays the one number it has always been. */
    totalCell(formatMoney(totalAllowances), 'right'),
  ]);
  return [
    sectionHeading('Labour Flow'),
    { table: { headerRows: 1, widths, body, dontBreakRows: true }, layout: TABLE_LAYOUT },
  ];
}

// 🔻 `buildAdhocLinesContent` + the `budget-crew-adhoc` section and CSV case
// were DELETED with the "Ad-hoc Budget Lines" tab (job cost codes, owner
// ruling 2026-10-28; pre-deletion SHA 4ae12235): an ad-hoc line is a JOB CODE
// now, and job codes print in the Cost Codes & Budget section above.

// Ordered section registry — keys/labels mirror BudgetCostCodes' SECTION_TABS
// (same ids), so the PDF sections stay in the same order the page shows the
// tabs in. Each entry knows how to pull its rows out of `data` and build its
// content slice.
const SECTIONS = [
  { key: 'budget-cost-codes', build: (data, opts) => buildCostCodesContent(data.costCodes, opts) },
  { key: 'budget-payment-types', build: (data) => buildPaymentTypesContent(data.paymentTypes) },
  { key: 'budget-crew-grades', build: (data, opts) => buildCrewGradesContent(data.grades, opts) },
  { key: 'budget-crew-members', build: (data, opts) => buildCrewMembersContent(data.members, opts) },
];

export const MANAGE_SECTION_KEYS = SECTIONS.map(s => s.key);

/* ── CSV export of ONE tab's table (2026-10-05, the Manage Budgets
   `Actions ▾`). Lives HERE, beside each tab's PDF builder, so a tab's columns
   have one home. The SAME columns as the PDF minus its print-only devices:
   nothing is truncated (a spreadsheet has no column budget), no Total row
   (a spreadsheet sums its own), and money is `formatMoney` text exactly as
   `BudgetCostCodes`' existing cost-codes export writes it.
   🔑 Labour Flow reads the SERVER-published figures the crew table shows —
   the rate the PDF prints (`effective_weekly_rate`, else the grade's),
   `total_weeks`, `budget_cost`, `allowance_total` — and derives nothing
   (`components/budget/CLAUDE.md`: THE SERVER COMPUTES THE MONEY). An ABSENT
   figure is an empty cell, never £0.00; a real 0 stays £0.00.
   Cost Codes is NOT here: its export is the page's `costCodesExportRow`
   (it needs the live-edited budget map and the adjustment gate). */
export function buildManageSectionCsv(key, data = {}, { formatMoney }) {
  const money = (v) => (v == null || v === '' ? '' : formatMoney(Number(v)));
  const date = (v) => (v ? formatDate(v) : '');
  switch (key) {
    case 'budget-payment-types':
      return {
        headers: ['Name', 'Description'],
        rows: (data.paymentTypes || []).map(pt => [pt.name, pt.description]),
      };
    case 'budget-crew-grades':
      return {
        headers: ['Grade Name', 'Weekly Rate', COMPUTER_ALLOWANCE_LABEL, 'Default Access Rights'],
        rows: (data.grades || []).map(g => [
          g.name, money(g.weekly_rate), money(g.computer_rental), g.default_role_name || 'None',
        ]),
      };
    case 'budget-crew-members':
      return {
        headers: [
          'Name', 'Grade', 'Location', 'Start', 'End', 'Weeks', 'Rate/wk',
          'Labour cost', 'Allowances',
        ],
        rows: (data.members || []).map(m => [
          m.name, m.grade_name, m.location, date(m.start_date), date(m.end_date),
          m.total_weeks != null ? String(m.total_weeks) : '',
          money(m.effective_weekly_rate != null ? m.effective_weekly_rate : m.grade_weekly_rate),
          money(m.budget_cost), money(m.allowance_total),
        ]),
      };
    default:
      return null;
  }
}

// ── Composer — full pdfmake docDefinition ───────────────────────────────
export function buildBudgetManagePdf({
  activeKeys = [],
  data = {},
  department,
  production,
  company,
  versionLabel,
  formatMoney,
}) {
  const selected = SECTIONS.filter(s => activeKeys.includes(s.key));
  if (selected.length === 0) return null;

  const opts = { formatMoney };
  const content = [];

  // Navy centred title strip (matches budgetVariancePdf.js's title strip).
  const subtitleParts = [production, company, versionLabel].filter(Boolean);
  content.push({
    stack: [
      {
        text: `${department ? `${department} ` : ''}Budget`,
        fontSize: 16, bold: true, color: NAVY, alignment: 'center',
      },
      ...(subtitleParts.length ? [{
        text: subtitleParts.join('  |  '),
        fontSize: 10, italics: true, color: '#555', alignment: 'center',
        margin: [0, 2, 0, 0],
      }] : []),
    ],
    margin: [0, 0, 0, 10],
  });

  selected.forEach((section, idx) => {
    const slice = section.build(data, opts);
    // Every section after the first starts on a fresh page.
    if (idx > 0 && slice.length > 0) {
      slice[0] = { ...slice[0], pageBreak: 'before' };
    }
    content.push(...slice);
  });

  const docTitle = `${department ? `${department} ` : ''}Budget`;

  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [28, 32, 28, 36],
    info: {
      title: docTitle,
      author: company || 'Crew Forge',
      subject: docTitle,
    },
    footer: (currentPage, pageCount) => ({
      columns: [
        {
          text: `Printed on ${dateShort()} | ${department || ''} Budget | Crew Forge`,
          fontSize: 7, color: FOOTER_GREY, alignment: 'left',
          margin: [28, 8, 0, 0],
        },
        {
          text: `Page ${currentPage} of ${pageCount}`,
          fontSize: 7, color: FOOTER_GREY, alignment: 'right',
          margin: [0, 8, 28, 0],
        },
      ],
    }),
    defaultStyle: { fontSize: 9, color: '#000' },
    content,
  };
}
